import * as React from "react";
import { AppBar, Toolbar, Stack, Typography, Box, Chip } from "@mui/material";

/**
 * Header with optional live connection pill.
 * Polls GET /api/health every 10s.
 */
type Props = {
  right?: React.ReactNode;
  showConnection?: boolean; // set false to hide the pill
};

export default function Header({ right, showConnection = true }: Props) {
  const [status, setStatus] = React.useState<"ok" | "down" | "loading">("loading");
  const [latency, setLatency] = React.useState<number | null>(null);

  React.useEffect(() => {
    if (!showConnection) return;
    let alive = true;

    const ping = async () => {
      const t0 = performance.now();
      try {
        const r = await fetch("/api/health", { cache: "no-store" });
        if (!alive) return;
        setStatus(r.ok ? "ok" : "down");
        setLatency(Math.round(performance.now() - t0));
      } catch {
        if (!alive) return;
        setStatus("down");
        setLatency(null);
      }
    };

    ping();
    const id = setInterval(ping, 10_000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [showConnection]);

  const host = typeof window !== "undefined" ? window.location.host : "esni.dev";

  return (
    <AppBar position="sticky" elevation={0} className="header-blur">
      <Toolbar sx={{ minHeight: 64 }}>
        <Stack direction="row" alignItems="center" gap={2} sx={{ width: "100%" }}>
          <Typography variant="h6">ESNI Service Admin</Typography>

          <Box sx={{ flex: 1 }} />

          {showConnection && (
            <Chip
              size="small"
              label={
                <span>
                  <span className={`dot ${status}`} /> {host}&nbsp;&nbsp;
                  {status === "ok" ? "Connected" : status === "loading" ? "Checking…" : "Offline"}
                  {status === "ok" && latency != null ? ` ${latency}ms` : ""}
                </span>
              }
              sx={{
                bgcolor: "rgba(255,255,255,0.05)",
                border: "1px solid rgba(255,255,255,0.10)",
                color: "#a0a0a0",
                ".MuiChip-label": { display: "flex", alignItems: "center" }
              }}
            />
          )}

          {right}
        </Stack>
      </Toolbar>
    </AppBar>
  );
}
